import { useState, useEffect, useCallback, useRef } from "react";
import "./ScreeningAgent.css";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { toast } from "react-toastify";
import Sidebarnew from "./NewUI/Sidebarnew";
import Navigationnew from "./NewUI/Navigationnew";

const EMPTY_DOC = {
  name: "",
  department: "",
  source: "",
  version: "",
};

function ScreeningAgent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [llmConfig, setLlmConfig] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);

  const [tone, setTone] = useState("Formal");
  const [verbosity, setVerbosity] = useState("Low");
  const [promptType, setPromptType] = useState("Prompt");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [showKbModal, setShowKbModal] = useState(false);
  const [kbDocumentsMaster, setKbDocumentsMaster] = useState([]);
  const [kbLoading, setKbLoading] = useState(false);
  const [kbError, setKbError] = useState(null); /* 🔥 FIX: Added missing error state */
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [attachedDocs, setAttachedDocs] = useState([]);

  // ✅ Advanced Tracking States for the Super-Filter
  const [sourceToggles, setSourceToggles] = useState({
    manual: true,
    drive: true,
    zapier: true,
    url: true,
  });

  // NEW UI State for Source Dropdown
  const [selectedSource, setSelectedSource] = useState("All");

  const [docFilters, setDocFilters] = useState({
    type: "All",
    department: "All",
    status: "All"
  });

  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [showShareModal, setShowShareModal] = useState(false);
  const [shareNameOrEmail, setShareNameOrEmail] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [sharePermissionOpen, setSharePermissionOpen] = useState(false);
  const [sharePermission, setSharePermission] = useState("view");
  
  const [shareableUsers, setShareableUsers] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [pageLoading, setPageLoading] = useState(true); // New state for overall page loading
  const dotsMenuRef = useRef(null);
  const userDropdownRef = useRef(null);
  
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchModels = async () => {
      // No individual loading state here, handled by pageLoading
      try {
        const res = await api.get("/llm-models");
        if (res.data) {
          setLlmConfig(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch LLM models");
      }
    };

    const loadInitialData = async () => {
      setPageLoading(true);
      await Promise.all([fetchModels(), fetchAgentData()]); // Assuming fetchAgentData is also awaited
      setPageLoading(false);
    };
    loadInitialData();
  }, []); // Removed fetchModels from dependency array, now part of loadInitialData

  const [visitedTabs, setVisitedTabs] = useState(new Set(["Basic Information"]));
  const [basicInfoTouched, setBasicInfoTouched] = useState(false);

  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState("Basic Information");
  const [hasChanges, setHasChanges] = useState(false);

  const [agent, setAgent] = useState({
    name: "",
    type: location.state?.templateName || "",
    provider: "",
    model: "",
    temperature: 0.5,
    description: "",
  });

  const tabs = [
    "Basic Information",
    "Prompt",
    "Knowledge Base",
  ];

  const isLastTab = activeTab === tabs[tabs.length - 1];

  const fetchAttachedDocs = async (agentId) => {
    try {
      const res = await api.get(`/agents/${agentId}/documents`);
      setAttachedDocs(res.data || []);
    } catch (err) {
      console.error("Failed to fetch attached docs");
    }
    // No individual loading state here, handled by pageLoading
  };

   const fetchAgentData = async () => {
      const existingAgent = location.state?.existingAgent;
      if (existingAgent) {
       setAgent({
  id: existingAgent.id,
  name: existingAgent.name || "",
  type: existingAgent.type || "",
  template_id: existingAgent.template_id || null,

  provider: existingAgent.provider || "",
  model: existingAgent.model || "",
  temperature: existingAgent.temperature || 0.5,
  description: existingAgent.description || "",
  created_at: existingAgent.created_at || null,
  updated_at: existingAgent.updated_at || null,
  doc_count: existingAgent.doc_count || 0,
  is_creator: existingAgent.is_creator,
});
        setTone(existingAgent.tone || "Formal");
        setVerbosity(existingAgent.verbosity || "Medium");
        setSystemPrompt(existingAgent.system_prompt || "");
        await fetchAttachedDocs(existingAgent.id);
        setIsEditMode(true);
        setHasChanges(false);
      }
    }
  useEffect(() => {
   
  }, [location.state]);

useEffect(() => {
    const templateData = location.state?.templateData;
    if (templateData && !location.state?.existingAgent) {
      setAgent((prev) => ({
        ...prev,
        type: templateData.type || location.state?.templateName || "",
        template_id: location.state?.templateId || null, // ✅ FIX: Save the template ID so the UI knows to lock the field!
        provider: templateData.provider || "",
        model: templateData.model || "",
        temperature: templateData.temperature ?? 0.5,
        description: templateData.description || "",
      }));
      setTone(templateData.tone || "Formal");
      setVerbosity(templateData.verbosity || "Medium");
      setPromptType(templateData.prompt_type || "Prompt");
      setSystemPrompt(templateData.system_prompt || "");
    }
  }, [location.state]); // Removed fetchAgentData from dependency array, now part of loadInitialData

  const isActiveAgent = attachedDocs.length > 0 || agent.doc_count > 0;

  const agentMetaConfig = [
    {
      key: "created_at",
      label: "Published On",
      format: (v) =>
        v
          ? new Date(v).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "-",
    },
    {
      key: "updated_at",
      label: "Last Updated On",
      format: (v) =>
        v
          ? new Date(v).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "-",
    },
    ...(isActiveAgent
      ? [
          { key: "rating", label: "Rating", format: (v) => v ?? "-" },
          {
            key: "responseTime",
            label: "Response Time",
            format: (v) => v ?? "-",
          },
        ]
      : []),
  ];

  const promptTypes = ["Prompt", "Custom Prompt"];
  const verbosities = ["Low", "Medium", "High"];
  const tones = ["Formal", "Neutral", "Friendly"];

  // ✅ New dropdown filter silently updates the original toggle state
  const handleSourceFilterChange = (e) => {
    const val = e.target.value;
    setSelectedSource(val);
    setSourceToggles({
      manual: val === "All" || val === "manual",
      drive: val === "All" || val === "drive",
      zapier: val === "All" || val === "zapier",
      url: val === "All" || val === "url",
    });
    markTabChanged();
  };

  const markTabChanged = () => {
    setHasChanges(true);
  };

/* 🔥 FIX: Added missing fetch function */
  const fetchKbDocuments = async () => {
    setKbLoading(true);
    setKbError(null);
    try {
      const res = await api.get("/documents");
      setKbDocumentsMaster(res.data.documents || []);
    } catch (err) {
      console.error("Failed to fetch KB documents:", err);
      setKbError("Failed to load documents. Please try again.");
    } finally {
      setKbLoading(false);
    }
  };

  /* 🔥 FIX: Updated open function to use the fetch */
  const handleOpenKbModal = () => {
    setSelectedDocs(attachedDocs.map((doc) => doc.id || doc._id));
    fetchKbDocuments();
    setShowKbModal(true);
  };

  /* 🔥 FIX: Added missing close function */
  const handleCloseKbModal = () => {
    setShowKbModal(false);
    setSelectedDocs([]);
    setKbError(null);
  };

  const handleToggleDoc = (docId) => {
    setSelectedDocs((prev) =>
      prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : [...prev, docId]
    );
  };

  const handleAttachDocs = () => {
    const finalDocs = [];
    selectedDocs.forEach((id) => {
      let doc = attachedDocs.find((d) => (d.id || d._id) === id);
      if (!doc) {
        doc = kbDocumentsMaster.find((d) => (d.id || d._id) === id);
      }
      if (doc) {
        finalDocs.push(doc);
      }
    });

    setAttachedDocs(finalDocs);
    markTabChanged();
    setShowKbModal(false);
  };

  const handleRemoveDoc = (docId) => {
    setAttachedDocs((prev) =>
      prev.filter((doc) => (doc.id || doc._id) !== docId)
    );
    markTabChanged();
  };

 const applyFilters = (docsArray) => {
    return docsArray.filter((doc) => {
      const src = doc.source_type || doc.source || "";
      const fType = (doc.file_type || "").toLowerCase();
      const docDep = doc.department ? doc.department.trim() : "None";
      const isVerified = doc.verified === true;

      let sourceMatch = false;
      if (sourceToggles.drive && src === 'google_drive') sourceMatch = true;
      else if (sourceToggles.zapier && src === 'zapier_crm') sourceMatch = true;
      else if (sourceToggles.url && src === 'url_scrape') sourceMatch = true;
      else if (sourceToggles.manual) sourceMatch = true; // ✅ FIX: If manual is checked, show all standard/local file uploads
      
      if (!sourceMatch) return false;

      if (docFilters.type !== "All") {
        if (docFilters.type === "PDF" && !fType.includes("pdf")) return false;
        if (docFilters.type === "Word" && !fType.includes("word") && !fType.includes("docx") && !fType.includes("doc")) return false;
        if (docFilters.type === "URL" && !fType.includes("url")) return false;
        if (docFilters.type === "Other" && (fType.includes("pdf") || fType.includes("word") || fType.includes("doc") || fType.includes("url"))) return false;
      }

      if (docFilters.department !== "All") {
        if (docDep !== docFilters.department) return false;
      }

      if (docFilters.status !== "All") {
        if (docFilters.status === "Verified" && !isVerified) return false;
        if (docFilters.status === "Not Verified" && isVerified) return false;
      }

      return true; 
    });
  };

  const allKnownDocs = [...attachedDocs, ...kbDocumentsMaster];
  const availableDepartments = [...new Set(allKnownDocs.map(d => d.department ? d.department.trim() : "None"))].filter(Boolean);

  const filteredAttachedDocs = applyFilters(attachedDocs);
  const filteredModalDocs = applyFilters(kbDocumentsMaster);

  const buildPayload = async (status = "draft") => {
    const wsRes = await api.get("/workspaces/check");
    const workspaceData = wsRes.data.workspace;
    if (!workspaceData) throw new Error("Workspace context not found.");

    return {
      name: agent.name,
      type: agent.type,
      provider: agent.provider,
      model: agent.model,
      description: agent.description,
      temperature: agent.temperature,
      system_prompt: systemPrompt,
      tone,
      verbosity,
      workspaceId: String(workspaceData.id),
      attachedDocIds: attachedDocs.map((doc) => doc.id || doc._id),
      status,
      template_id: location.state?.templateId || agent.template_id,
    };
  };

  const handleSaveAndExit = async () => {
    try {
      const payload = await buildPayload("draft");
      const passedTemplateId = location.state?.templateId;

      if (agent.id) {
        await api.put(`/agents/${agent.id}`, payload);
      } else {
        await api.post("/agents", {
          ...payload,
          is_predefined_agent: true,
          template_id: passedTemplateId,
        });
      }
      setHasChanges(false);
      navigate("/home");
    } catch (err) {
   toast.error(err.response?.data?.message || err.message);
    }
  };

  const handleSaveAndNext = async () => {
    const currentIndex = tabs.indexOf(activeTab);

    if (activeTab === "Basic Information" && !agent.id && basicInfoTouched) {
      try {
        const payload = await buildPayload("draft");
        const res = await api.post("/agents", {
          ...payload,
          is_predefined_agent: true,
          template_id: location.state?.templateId,
        });
        setAgent((prev) => ({ ...prev, id: res.data.id }));
      } catch (err) {
        console.error("Initial draft save failed");
      }
    }

    if (isLastTab) {
      try {
        const payload = await buildPayload("draft");
        const passedTemplateId = location.state?.templateId;

        if (agent.id) {
          await api.put(`/agents/${agent.id}`, payload);
        } else {
          await api.post("/agents", {
            ...payload,
            is_predefined_agent: true,
            template_id: passedTemplateId,
          });
        }
        setHasChanges(false);
        navigate("/home");
      } catch (err) {
       toast.error(err.response?.data?.message || err.message);
      }
    } else {
      const nextTab = tabs[currentIndex + 1];
      setActiveTab(nextTab);
      setVisitedTabs((prev) => new Set([...prev, nextTab]));
    }
  };

  const handleDeploy = async () => {
    try {
      const payload = await buildPayload("active");
      const passedTemplateId = location.state?.templateId;

      if (agent.id) {
        await api.put(`/agents/${agent.id}`, payload);
     toast.success("Agent updated and activated successfully");
      } else {
        await api.post("/agents", {
          ...payload,
          is_predefined_agent: true,
          template_id: passedTemplateId,
        });
    toast.success("Agent deployed successfully");
      }
      setHasChanges(false);
      navigate("/home");
    } catch (err) {
    toast.error(err.response?.data?.message || err.message);
    }
  };

  const handleGlobalUpdate = async () => {
    try {
      const payload = await buildPayload(agent.status || "draft");
      await api.put(`/agents/${agent.id}`, payload);
   toast.success("Agent updated successfully");
      setHasChanges(false);
      navigate("/home");
    } catch (err) {
    toast.error(err.response?.data?.message || err.message);
    }
  };

  const handleDeleteAgent = async () => {
    try {
      await api.delete(`/agents/${agent.id}`);
      setShowDeleteConfirm(false);
      navigate("/home");
    } catch (err) {
      toast.error(
  err.response?.data?.message || err.message
);
    }
  };

 useEffect(() => {
    if (showShareModal && agent.id) {
      // ✅ CACHE BUSTER: Always get the real-time list of who has access
      api.get(`/agents/${agent.id}/shareable-users?t=${new Date().getTime()}`)
        .then(res => setShareableUsers(res.data))
        .catch(err => console.error("Error fetching shareable users:", err));
    }
  }, [showShareModal, agent.id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  useEffect(() => {
    const handleDotsOutsideClick = (e) => {
      if (dotsMenuRef.current && !dotsMenuRef.current.contains(e.target)) {
        setShowDotsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleDotsOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleDotsOutsideClick);
    };
  }, []);

  const filteredUsers = shareableUsers.filter(u => 
    u.email.toLowerCase().includes(shareNameOrEmail.toLowerCase()) || 
    u.name.toLowerCase().includes(shareNameOrEmail.toLowerCase())
  );

  // ✅ UPDATED handleDirectShare with Gemini's changes
const handleDirectShare = async () => {
    if (!shareNameOrEmail.trim()) {
      return toast.warning("Please select or enter an email address");
    }

    const validUser = shareableUsers.find(u => u.email === shareNameOrEmail);
    if (!validUser) {
      return toast.error("Person is not linked to your account");
    }

    const isUpdate = Boolean(validUser.has_access);

    try {
      setIsSharing(true);
      
      await api.post(`/agents/${agent.id}/direct-share`, {
        targetEmail: shareNameOrEmail,
        permissionType: sharePermission,
        customMessage: shareMessage
      });

      // 1. Close the modal FIRST so it leaves the DOM
      setShowShareModal(false);
      setShareNameOrEmail("");
      setShareMessage("");
      setSharePermission("view"); 

      // ✅ FIX: Added cache buster to the background refresh so the state is actually updated
      api.get(`/agents/${agent.id}/shareable-users?t=${new Date().getTime()}`)
        .then(res => setShareableUsers(res.data))
        .catch(() => {});

      // 2. Fire the toast AFTER a small delay (300ms)
      setTimeout(() => {
        if (isUpdate) {
          toast.success("Agent permissions updated successfully");
        } else {
          toast.success("Agent shared successfully");
        }
      }, 300);

    } catch (err) {
      // Also delay the error toast
      setTimeout(() => {
        toast.error(err.response?.data?.message || "Failed to share agent");
      }, 300);
    } finally {
      setIsSharing(false);
    }
  };
  const canDeployFlag = attachedDocs.length > 0;

 return (
    <>
      <div className="db-page-layout">

        {/* SIDEBAR */}
        <aside className="db-sidebar-shell">
          <Sidebarnew />
        </aside>

        {/* MAIN AREA */}
        <div className="db-main-wrapper">

          {/* TOP NAVBAR (Locked at the top) */}
          <div className="db-topbar-shell screening-topbar-fix">
            <Navigationnew
              setArtifactsOpen={() => {}}
              artifactsOpen={false}
            />
          </div>

          {/* SCROLLABLE PAGE AREA */}
          {/* 🔥 FIX 1: Removed 'w-100' so the scrollbar renders properly on the edge */}
          <div className="agent-creation-container">
            
            {/* 🔥 FIX 2: Header moved INSIDE the scroll container so it never overlaps the cards */}
            <div className="create-agent-header">
              <h1>{agent?.name || "Screening Agent"}</h1>
              <p>AI-powered screening and workflow agent</p>
            </div>

            <div className="screening-agent-content">
           <div className="p-3 pb-2">
                <div className="bg-white rounded-3 shadow-sm border border-light">
<div className="px-3 py-2 rounded-top bg-light border-bottom d-flex align-items-center gap-2">
                      <img src="/images/antenna.svg" alt="icon" style={{ width: "22px" }} />
                    <h6 className="fw-semibold mb-0 text-dark">About</h6>
                  </div>

              <div className="px-3 py-2">
                 <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>{agent.description || "Manages tasks and suggests optimal workflows."}</p>
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                      <div className="d-flex gap-5 flex-wrap">
                        {agentMetaConfig.map((item) => (
                          <Meta
                            key={item.key}
                            label={item.label}
                            value={
                              item.format
                                ? item.format(agent[item.key])
                                : agent[item.key] ?? "-"
                            }
                          />
                        ))}
                      </div>

                      {/* ─── Chat button + dots menu with Delete & Share ─── */}
                      <div className="d-flex align-items-center gap-2">
                        {isActiveAgent && agent.id && (
                         <button
  className="chat-agent-btn"
  onClick={() => navigate(`/ChatInterface/${agent.id}`)}
>
  Chat
</button>
                        )}

                        {agent.id && agent.is_creator === true && (
                          <div className="position-relative" ref={dotsMenuRef}>
                            <button
                              className="btn btn-light border"
                              onClick={() => setShowDotsMenu((prev) => !prev)}
                            >
                              <i className="bi bi-three-dots-vertical"></i>
                            </button>

                            {showDotsMenu && (
                              <div
                                className="position-absolute bg-white border rounded shadow-sm"
                                style={{
                                  right: 0,
                                  top: "110%",
                                  zIndex: 999,
                                  minWidth: "120px",
                                }}
                              >
                                <button
                                  className="btn btn-link text-danger w-100 text-start px-3 py-2"
                                  style={{ textDecoration: "none" }}
                                  onClick={() => {
                                    setShowDotsMenu(false);
                                    setShowDeleteConfirm(true);
                                  }}
                                >
                                  Delete
                                </button>

                                {isActiveAgent && (
                                  <button
                                    className="btn btn-link text-dark w-100 text-start px-3 py-2"
                                    style={{ textDecoration: "none" }}
                                    onClick={() => {
                                      setShowDotsMenu(false);
                                      setShareNameOrEmail("");
                                      setShareMessage("");
                                      setSharePermission("view");
                                      setSharePermissionOpen(false);
                                      setShowShareModal(true);
                                    }}
                                  >
                                    Share
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

         <div className="px-3 pb-0 pt-2">
                <div className="form-card shadow-sm w-100" style={{ overflow: "visible" }}>
                  <div className="tabs-header-bar d-flex">
                    {tabs.map((tab) => (
                      <button
                        key={tab}
                        className={`tab-link-blended ${activeTab === tab ? "active" : ""}`}
                        onClick={() => setActiveTab(tab)}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="form-content-body" style={{ overflow: "visible" }}>
                    {activeTab === "Basic Information" && pageLoading ? (
                      <div key="basic-skeleton" className="tab-panel-enter p-4 placeholder-glow">
                        <div className="row">
                          <div className="col-md-6 d-flex flex-column gap-4">
                            <div>
                              <label className="form-label-custom placeholder col-3"></label>
                              <div className="form-control input-background placeholder col-12" style={{ height: '38px' }}></div>
                            </div>
                            <div>
                              <label className="form-label-custom placeholder col-3"></label>
                              <div className="form-control input-background placeholder col-12" style={{ height: '38px' }}></div>
                            </div>
                            <div>
                              <label className="form-label-custom placeholder col-3"></label>
                              <div className="form-control input-background placeholder col-12" style={{ height: '38px' }}></div>
                            </div>
                            <div>
                              <label className="form-label-custom placeholder col-3"></label>
                              <div className="form-control input-background placeholder col-12" style={{ height: '38px' }}></div>
                            </div>
                          </div>
                          <div className="col-md-6 d-flex flex-column gap-4">
                            <div>
                              <label className="form-label-custom placeholder col-3"></label>
                              <div
                                className="form-control input-background placeholder col-12"
                                style={{ height: "42px" }}
                              ></div>
                            </div>
                            <div className="d-flex flex-column flex-grow-1">
                              <label className="form-label-custom placeholder col-3"></label>
                              <div
                                className="form-control input-background placeholder col-12 flex-grow-1"
                                style={{ minHeight: "110px", resize: "none" }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : activeTab === "Basic Information" && (
                      <div key="basic" className="tab-panel-enter p-3" style={{ overflow: "visible" }}>
                        <div className="row">
                          {/* Left Column */}
                          <div className="col-md-6 d-flex flex-column gap-4">
                            <div>
                              <label className="form-label-custom">Agent Name</label>
                              <input
                                type="text"
                                name="name"
                                className="form-control input-background"
                                placeholder="Sarah Johnson"
                                value={agent?.name || ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setAgent({ ...agent, name: value });
                                  markTabChanged();
                                  setBasicInfoTouched(value.length > 0);
                                }}
                              />
                            </div>
                            <div>
                              <label className="form-label-custom">LLM Model</label>
                              <select
                                name="provider"
                                className="form-select input-background"
                                value={agent?.provider || ""}
                                onChange={(e) => {
                                  const provider = e.target.value;
                                  setAgent({ ...agent, provider, model: "" });
                                  markTabChanged();
                                }}
                              >
                                <option value="" disabled hidden>Select Provider</option>
                                {Object.keys(llmConfig || {}).map((provider) => (
                                  <option key={provider} value={provider}>{provider}</option>
                                ))}
                              </select>
                            </div>
                            <div style={{ position: "relative" }}>
                              <label className="form-label-custom">Model Version</label>
                              <div
                                className="form-control input-background d-flex justify-content-between align-items-center"
                                style={{
                                  cursor: (!agent.provider) ? "not-allowed" : "pointer",
                                  opacity: (!agent.provider) ? 0.6 : 1,
                                  userSelect: "none",
                                }}
                                onClick={() => {
                                  if (!agent.provider) return;
                                  setModelDropdownOpen((prev) => !prev);
                                }}
                              >
                                <span style={{ color: agent.model ? "#1f2937" : "#9ca3af" }}>
                                  {agent.model || "Select Model"}
                                </span>
                                <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>▼</span>
                              </div>

                              {modelDropdownOpen && (
                                <>
                                  <div
                                    style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                                    onClick={() => setModelDropdownOpen(false)}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      right: 0,
                                      background: "#fff",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      zIndex: 1000,
                                      maxHeight: "220px",
                                      overflowY: "auto",
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                      marginTop: "2px",
                                    }}
                                  >
                                    {(llmConfig?.[agent.provider] || []).map((model) => (
                                      <div
                                        key={model}
                                        style={{
                                          padding: "9px 14px",
                                          fontSize: "0.875rem",
                                          cursor: "pointer",
                                          background: agent.model === model ? "#eff6ff" : "transparent",
                                          color: agent.model === model ? "#2563eb" : "#374151",
                                        }}
                                        onMouseEnter={(e) => { if (agent.model !== model) e.target.style.background = "#f9fafb"; }}
                                        onMouseLeave={(e) => { if (agent.model !== model) e.target.style.background = "transparent"; }}
                                        onClick={() => {
                                          setAgent({ ...agent, model });
                                          markTabChanged();
                                          setModelDropdownOpen(false);
                                        }}
                                      >
                                        {model}
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                            <div>
                              <label className="form-label-custom">Temperature</label>
                              <input
                                type="range"
                                name="temperature"
                                className="slider-blue w-100 mt-2"
                                min="0" max="1" step="0.1"
                                value={agent?.temperature ?? 0.5}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  setAgent({ ...agent, temperature: value });
                                  markTabChanged();
                                }}
                                style={{
                                  background: `linear-gradient(to right, #2563EB 0%, #2563EB ${
                                    (agent?.temperature ?? 0.5) * 100
                                  }%, #E5E7EB ${(agent?.temperature ?? 0.5) * 100}%, #E5E7EB 100%)`
                                }}
                              />
                            </div>
                          </div>

                          {/* Right Column */}
                          <div className="col-md-6 d-flex flex-column gap-4">
     <div>
                              <label className="form-label-custom">Type</label>
                             {(agent?.template_id || location.state?.templateId) ? ( /* ✅ FIX: Check both agent state and location state */
  <div
    className="form-control input-background d-flex align-items-center"
    style={{ height: "42px", color: "#6B7280", cursor: "not-allowed", backgroundColor: "#f3f4f6" }}
  >
    {agent?.type || "Predefined Template"}
  </div>
) : (
  <input
    type="text"
    className="form-control input-background"
    value={agent?.type || ""}
    placeholder="Enter agent type"
    onChange={(e) => {
      setAgent({ ...agent, type: e.target.value });
      markTabChanged();
    }}
  />
)}
                            </div>
                            <div className="d-flex flex-column flex-grow-1">
                              <label className="form-label-custom">Description</label>
                              <textarea
                                name="description"
                                className="form-control input-background flex-grow-1"
                                style={{ minHeight: "110px", resize: "none" }}
                                placeholder="Description"
                                value={agent?.description || ""}
                                onChange={(e) => {
                                  setAgent({ ...agent, description: e.target.value });
                                  markTabChanged();
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === "Prompt" && pageLoading ? (
                      <div key="prompt-skeleton" className="tab-panel-enter p-4 placeholder-glow">
                        <div className="row">
                          <div className="col-md-4 d-flex flex-column gap-4">
                            <div>
                              <label className="form-label-custom placeholder col-4"></label>
                              <div className="form-select input-background placeholder col-12" style={{ height: '38px' }}></div>
                            </div>
                            <div>
                              <label className="form-label-custom placeholder col-4"></label>
                              <div className="form-select input-background placeholder col-12" style={{ height: '38px' }}></div>
                            </div>
                          </div>
                          <div className="col-md-8 d-flex flex-column">
                            <label className="form-label-custom placeholder col-3"></label>
                            <div
                              className="form-control border-0 input-background placeholder col-12 flex-grow-1"
                              style={{ minHeight: "140px", resize: "none" }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ) : activeTab === "Prompt" && (
                      <div key="prompt" className="tab-panel-enter p-4">
                        <div className="row">
                          <div className="col-md-4 d-flex flex-column gap-4">
                            <div>
                              <label className="form-label-custom">Tone</label>
                              <select 
                                name="tone" 
                                className="form-select input-background" 
                                value={tone} 
                                onChange={(e) => { setTone(e.target.value); markTabChanged(); }}
                              >
                                <option>Formal</option>
                                <option>Neutral</option>
                                <option>Friendly</option>
                              </select>
                            </div>
                            <div>
                              <label className="form-label-custom">Verbosity</label>
                              <select 
                                name="verbosity" 
                                className="form-select input-background" 
                                value={verbosity} 
                                onChange={(e) => { setVerbosity(e.target.value); markTabChanged(); }}
                              >
                                <option>Low</option>
                                <option>Medium</option>
                                <option>High</option>
                              </select>
                            </div>
                          </div>
                          <div className="col-md-8 d-flex flex-column">
                            <label className="form-label-custom">System Prompt</label>
                            <textarea
                              name="system_prompt"
                              className="form-control border-0 input-background flex-grow-1"
                              style={{ minHeight: "140px", resize: "none" }}
                              placeholder="Description"
                              value={systemPrompt}
                              onChange={(e) => { setSystemPrompt(e.target.value); markTabChanged(); }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === "Knowledge Base" && pageLoading ? (
                      <div key="kb-skeleton" className="tab-panel-enter d-flex flex-column h-100 placeholder-glow">
                        <div className="d-flex justify-content-between align-items-end px-4 py-3 border-bottom bg-white">
                          <div className="d-flex gap-3">
                            <div style={{ width: "170px" }}>
                              <div className="d-block mb-1 text-muted form-label-custom placeholder col-6"></div>
                              <div className="form-select bg-white border placeholder col-12" style={{ height: '38px' }}></div>
                            </div>
                            <div style={{ width: "170px" }}>
                              <div className="d-block mb-1 text-muted form-label-custom placeholder col-6"></div>
                              <div className="form-select bg-white border placeholder col-12" style={{ height: '38px' }}></div>
                            </div>
                            <div style={{ width: "170px" }}>
                              <div className="d-block mb-1 text-muted form-label-custom placeholder col-6"></div>
                              <div className="form-select bg-white border placeholder col-12" style={{ height: '38px' }}></div>
                            </div>
                          </div>
                          <div className="d-flex align-items-end gap-3">
                            <div style={{ width: "170px" }}>
                              <div className="d-block mb-1 text-muted form-label-custom placeholder col-6"></div>
                              <div className="form-select bg-white border placeholder col-12" style={{ height: '38px' }}></div>
                            </div>
                            <button className="btn-solid-blue placeholder col-4" style={{ height: '38px' }}></button>
                          </div>
                        </div>
                        <div className="kb-table-scroll-container bg-white">
                          <table className="table kb-custom-table align-middle mb-0">
                            <thead>
                              <tr>
                                <th><div className="placeholder col-8"></div></th>
                                <th><div className="placeholder col-6"></div></th>
                                <th><div className="placeholder col-6"></div></th>
                                <th><div className="placeholder col-4"></div></th>
                                <th><div className="placeholder col-8"></div></th>
                                <th><div className="placeholder col-6"></div></th>
                                <th style={{ textAlign: "center" }}><div className="placeholder col-4"></div></th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...Array(5)].map((_, index) => (
                                <tr key={index}>
                                  <td><div className="placeholder col-10"></div></td>
                                  <td><div className="placeholder col-6"></div></td>
                                  <td><div className="placeholder col-8"></div></td>
                                  <td><div className="placeholder col-4"></div></td>
                                  <td><div className="placeholder col-8"></div></td>
                                  <td><div className="placeholder col-6"></div></td>
                                  <td style={{ textAlign: "center" }}><div className="placeholder col-4"></div></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : activeTab === "Knowledge Base" && (
                      <div key="kb" className="tab-panel-enter d-flex flex-column h-100">
                        
                        {/* Top Filter Bar */}
                        <div className="d-flex justify-content-between align-items-end px-4 py-3 border-bottom bg-white">
                          
                          {/* Left Filters */}
                          <div className="d-flex gap-3">
                            <div style={{ width: "170px" }}>
                              <label className="d-block mb-1 text-muted form-label-custom">Type</label>
                              <select 
                                className="form-select bg-white border"
                                value={docFilters.type}
                                onChange={(e) => setDocFilters(prev => ({ ...prev, type: e.target.value }))}
                              >
                                <option value="All">All Types</option>
                                <option value="PDF">PDF</option>
                                <option value="Word">Word Document</option>
                                <option value="URL">URL / Web Data</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>

                            <div style={{ width: "170px" }}>
                              <label className="d-block mb-1 text-muted form-label-custom">Department</label>
                              <select 
                                className="form-select bg-white border"
                                value={docFilters.department}
                                onChange={(e) => setDocFilters(prev => ({ ...prev, department: e.target.value }))}
                              >
                                <option value="All">All Departments</option>
                                {availableDepartments.map(dep => (
                                  <option key={dep} value={dep}>{dep}</option>
                                ))}
                              </select>
                            </div>

                            <div style={{ width: "170px" }}>
                              <label className="d-block mb-1 text-muted form-label-custom">Status</label>
                              <select 
                                className="form-select bg-white border"
                                value={docFilters.status}
                                onChange={(e) => setDocFilters(prev => ({ ...prev, status: e.target.value }))}
                              >
                                <option value="All">All Statuses</option>
                                <option value="Verified">Verified</option>
                                <option value="Not Verified">Not Verified</option>
                              </select>
                            </div>
                          </div>

                          {/* Right Filters & Add Button */}
                          <div className="d-flex align-items-end gap-3">
                            <div style={{ width: "170px" }}>
                              <label className="d-block mb-1 text-muted form-label-custom">Source Integration</label>
                              <select 
                                className="form-select bg-white border" 
                                value={selectedSource} 
                                onChange={handleSourceFilterChange} 
                              >
                                <option value="All">All Sources</option>
                                <option value="manual">Manual</option>
                                <option value="drive">Drive</option>
                                <option value="zapier">Zapier</option>
                                <option value="url">URL Scraping</option>
                              </select>
                            </div>
                            <button className="btn-solid-blue" onClick={handleOpenKbModal}>
                              Add New
                            </button>
                          </div>

                        </div>

                        {/* Table Area */}
                        <div className="kb-table-scroll-container bg-white">
                          <table className="table kb-custom-table align-middle mb-0">
                            <thead>
                              <tr>
                                <th>Document Name</th>
                                <th>Department</th>
                                <th>Source</th>
                                <th>Version</th>
                                <th>Verification Status</th>
                                <th>Last updated</th>
                                <th style={{ textAlign: "center" }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredAttachedDocs.length > 0 ? (
                                filteredAttachedDocs.map((doc) => (
                                  <tr key={doc.id || doc._id}>
                                    <td className="text-dark fw-medium">
                                      <div className="d-flex align-items-center gap-2">
                                        <i className="bi bi-robot text-primary fs-5"></i>
                                        {doc.file_name || doc.name || "Untitled"}
                                      </div>
                                    </td>
                                    <td>{doc.department || "-"}</td>
                                    <td>
                                      <span className="source-badge">
                                        {doc.source_type || doc.source || "MANUAL"}
                                      </span>
                                    </td>
                                    <td>{doc.version || "v1.0"}</td>
                                    <td>
                                      <span className={doc.verified ? "status-verified" : "status-not-verified"}>
                                        {doc.verified ? "VERIFIED" : "NOT VERIFIED"}
                                      </span>   
                                    </td>   
                                    <td>{doc.updated || "Just Now"}</td>
                                    <td style={{ textAlign: "center" }}>
                                      <div className="d-flex align-items-center justify-content-center">
                                        <i 
                                          className="bi bi-trash text-danger" 
                                          style={{ cursor: "pointer", fontSize: "1.1rem" }}
                                          onClick={() => handleRemoveDoc(doc.id || doc._id)}
                                        ></i>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                  <tr>
                                  <td colSpan="7" className="text-center py-5 text-muted fst-italic">
                                    {attachedDocs.length === 0 
                                      ? "No documents attached. Please click 'Add New' to attach documents."
                                      : "No attached documents match the current filters."}
                                    </td>
                                  </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                      </div>
                    )}
                  </div>
                </div>

                {/* ─── NEW CLEAN FLOATING FOOTER BUTTONS ─── */}
                <div className="d-flex justify-content-end align-items-center gap-3 mt-3 mb-1">
                  {isEditMode ? (
                    <button
                      className="btn-solid-blue px-4"
                      onClick={handleGlobalUpdate}
                      disabled={!hasChanges}
                    >
                      Update
                    </button>
                  ) : (
                    <>
                      <button
                        className="btn-save-exit"
                        onClick={handleSaveAndExit}
                        disabled={!basicInfoTouched}
                      >
                        Save & Exit
                      </button>

                      <button
                        className="btn-save-next"
                        onClick={handleSaveAndNext}
                        disabled={!basicInfoTouched}
                      >
                        {isLastTab ? "Save & Finish" : "Save & Next"}
                      </button>

                      <button
                        className="btn-deploy-agent"
                        onClick={handleDeploy}
                        disabled={!canDeployFlag}
                        style={{ opacity: canDeployFlag ? 1 : 0.4, cursor: canDeployFlag ? "pointer" : "not-allowed" }}
                      >
                        Deploy Agent
                      </button>
                    </>
                  )}
                </div>

              </div>
            </div>
          </div>

          {/* ─── Delete Confirmation Modal ──────────────────────────────── */}
          {showDeleteConfirm && (
           <div
  className="modal fade show d-block"
  tabIndex="-1"
  style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 11000, pointerEvents: 'none' }}
>
  <div
    className="modal-dialog modal-dialog-centered"
    style={{ maxWidth: "420px", pointerEvents: 'auto' }}  // ← this is missing
  >
                <div className="modal-content rounded-4 p-2">
                  <div className="modal-header border-0 pb-0">
                    <h5 className="modal-title fw-bold">Delete Agent</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowDeleteConfirm(false)}
                    ></button>
                  </div>
                  <div className="modal-body py-3">
                    <p className="text-muted mb-0">
                      Are you sure you want to delete{" "}
                      <strong>{agent.name}</strong>? This action cannot be
                      undone.
                    </p>
                  </div>
                  <div className="modal-footer border-0 pt-0">
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-4"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger px-4"
                      onClick={handleDeleteAgent}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── Share Agent Modal ───────────────────────────────────────── */}
          {showShareModal && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              style={{ backgroundColor: "rgba(0,0,0,0.4)", zIndex: 11000 }}
              onClick={() => {
                setShowShareModal(false);
                setSharePermissionOpen(false);
                setShowUserDropdown(false);
              }}        
            >
              <div
                className="modal-dialog modal-dialog-centered"
                style={{ maxWidth: "460px" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content rounded-4 p-1">
                  <div className="modal-header border-bottom pb-3 pt-3 px-4">
                    <h5 className="modal-title fw-bold" style={{ fontSize: "17px" }}>
                      Share Agent
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setShowShareModal(false);
                        setSharePermissionOpen(false);
                        setShowUserDropdown(false);
                      }}                
                    ></button>
                  </div>

                  <div className="modal-body px-4 pt-3 pb-4">
                    <div className="mb-3 position-relative" ref={userDropdownRef}>
                      <label
                        className="form-label text-muted mb-1"
                        style={{ fontSize: "14px" }}
                      >
                        Name
                      </label>
                      <div
                        className="d-flex align-items-center rounded-3 px-3 position-relative"
                        style={{
                          backgroundColor: "#f1f3f5",
                          border: "1px solid #e9ecef",
                        }}
                        onClick={() => setShowUserDropdown(true)}
                      >
                        <input
                          type="text"
                          className="form-control border-0 bg-transparent shadow-none ps-0"
                          placeholder="Select user by Name or Email"
                          value={shareNameOrEmail}
                          onChange={(e) => {
                            setShareNameOrEmail(e.target.value);
                            setShowUserDropdown(true);
                          }}
                          style={{ fontSize: "14px" }}
                        />

                        <div className="position-relative d-flex align-items-center flex-shrink-0 ms-2">
                          <button
                            type="button"
                            className="btn p-0 border-0 bg-transparent d-flex align-items-center gap-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSharePermissionOpen((prev) => !prev);
                              setShowUserDropdown(false);
                            }}
                          >
                           {shareNameOrEmail && (
  <i
    className={`bi ${sharePermission === "view" ? "bi-eye" : "bi-pencil"}`}
    style={{ fontSize: "16px", color: "#6c757d" }}
  ></i>
)}
                            <i
                              className="bi bi-chevron-down"
                              style={{ fontSize: "13px", color: "#6c757d" }}
                            ></i>
                          </button>

                          {sharePermissionOpen && (
                            <div
                              className="position-absolute bg-white border rounded-3 shadow-sm"
                              style={{
                                top: "calc(100% + 8px)",
                                right: 0,
                                minWidth: "110px",
                                zIndex: 12000,
                                overflow: "hidden",
                              }}
                            >
                              <button
                                type="button"
                                className="btn w-100 text-start border-0 bg-white d-flex align-items-center gap-2 px-3 py-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSharePermission("view");
                                  setSharePermissionOpen(false);
                                }}
                              >
                                <i
                                  className="bi bi-eye"
                                  style={{ fontSize: "15px", color: "#6c757d" }}
                                ></i>
                                <span style={{ fontSize: "14px", color: "#495057" }}>View</span>
                              </button>

                              <button
                                type="button"
                                className="btn w-100 text-start border-0 bg-white d-flex align-items-center gap-2 px-3 py-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSharePermission("edit");
                                  setSharePermissionOpen(false);
                                }}
                              >
                                <i
                                  className="bi bi-pencil"
                                  style={{ fontSize: "15px", color: "#6c757d" }}
                                ></i>
                                <span style={{ fontSize: "14px", color: "#495057" }}>Edit</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {showUserDropdown && (
                        <div 
                          className="position-absolute bg-white border rounded-3 shadow-sm w-100 mt-1"
                          style={{ zIndex: 12000, maxHeight: "200px", overflowY: "auto" }}
                        >
                          {filteredUsers.length === 0 ? (
                            <div className="p-3 text-muted small text-center">
                              No eligible users found.
                            </div>
                          ) : (
                            // ✅ UPDATED filteredUsers.map with Gemini's changes
                            filteredUsers.map((user) => (
                              <div 
                                key={user.common_id}
                                className="px-3 py-2 border-bottom"
                                style={{ cursor: "pointer", transition: "background 0.2s" }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                onClick={() => {
                                  setShareNameOrEmail(user.email);
                                  setShowUserDropdown(false);
                                  // ✅ NEW: Automatically select their current permission in the UI
                                  if (user.has_access) {
                                    setSharePermission(user.can_edit ? "edit" : "view");
                                  } else {
                                    setSharePermission("view");
                                  }
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="fw-semibold text-dark" style={{ fontSize: "13px" }}>{user.name}</div>
                                    <div className="text-muted" style={{ fontSize: "12px" }}>{user.email}</div>
                                  </div>
                                  {/* ✅ NEW: Show an indicator if they already have access */}
                                  {user.has_access && (
                                    <span className="badge bg-success-subtle text-success" style={{fontSize: "9px", padding: "4px 6px"}}>Shared</span>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    <div className="mb-0">
                      <label
                        className="form-label text-muted mb-1"
                        style={{ fontSize: "14px" }}
                      >
                        Message
                      </label>
                      <textarea
                        className="form-control rounded-3"
                        placeholder="Add an optional message..."
                        rows="4"
                        value={shareMessage}
                        onChange={(e) => setShareMessage(e.target.value)}
                        style={{
                          backgroundColor: "#f1f3f5",
                          border: "1px solid #e9ecef",
                          fontSize: "14px",
                          resize: "none",
                        }}
                      />
                    </div>
                  </div>

                  <div
                    className="modal-footer border-0 pt-0 pb-3 px-4"
                    style={{ justifyContent: "flex-end" }}
                  >
                    {/* ✅ UPDATED button text with Gemini's changes */}
                    <button
                      type="button"
                      className="btn px-4 py-2"
                      onClick={handleDirectShare}
                      disabled={isSharing}
                      style={{
                        backgroundColor: "#1D5DA8",
                        color: "#fff",
                        borderRadius: "8px",
                        fontWeight: "500",
                        fontSize: "14px",
                        border: "none",
                      }}
                    >
                      {/* ✅ NEW: Button dynamically changes text */}
                      {isSharing 
                        ? "Processing..." 
                        : (shareableUsers.find(u => u.email === shareNameOrEmail)?.has_access ? "Update Access" : "Share")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============ KB DOCUMENT SELECTION MODAL ============ */}
          {showKbModal && (
            <div className="kb-modal-backdrop" onClick={handleCloseKbModal}>
              <div className="kb-modal-box" onClick={(e) => e.stopPropagation()}>

                <div className="kb-modal-header">
                  <div>
                    <h6 className="kb-modal-title">Select Documents</h6>
                    <p className="kb-modal-subtitle">Choose documents from your Knowledge Base to attach to this agent</p>
                  </div>
                  <span className="kb-modal-close" onClick={handleCloseKbModal}>×</span>
                </div>

                <div className="kb-modal-body">
                  {kbLoading && (
                    <div className="kb-modal-state">
                      <div className="spinner-border text-primary" role="status"></div>
                      <p className="mt-3 text-muted small">Loading documents...</p>
                    </div>
                  )}

                  {kbError && !kbLoading && (
                    <div className="kb-modal-state">
                      <p className="text-danger small">{kbError}</p>
                      <button className="btn btn-sm btn-outline-primary mt-2" onClick={fetchKbDocuments}>Retry</button>
                    </div>
                  )}

                  {!kbLoading && !kbError && filteredModalDocs.length === 0 && (
                    <div className="kb-modal-state">
                      <p className="text-muted small">No documents found matching your active filters and toggles.</p>
                    </div>
                  )}

                  {!kbLoading && !kbError && filteredModalDocs.length > 0 && (
                    <div className="table-responsive">
                      <table className="table kb-select-table align-middle mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "60px", textAlign: "center" }}>
                              <input
                                type="checkbox"
                                className="custom-checkbox"
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedDocs(filteredModalDocs.map(d => d.id || d._id));
                                  } else {
                                    setSelectedDocs([]);
                                  }
                                }}
                                checked={filteredModalDocs.length > 0 && selectedDocs.length === filteredModalDocs.length}
                              />
                            </th>
                            <th>Document Name</th>
                            <th>Department</th>
                            <th>Source</th>
                            <th>Verification Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredModalDocs.map((doc) => {
                            const docId = doc.id || doc._id;
                            const isSelected = selectedDocs.includes(docId);
                            return (
                              <tr key={docId} className={isSelected ? "kb-row-selected" : ""}>
                                <td style={{ textAlign: "center" }}>
                                  <input
                                    type="checkbox"
                                    className="custom-checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleToggleDoc(docId);
                                    }}
                                  />
                                  </td>
                                <td className={isSelected ? "text-primary fw-medium" : "fw-medium"}>
                                  {doc.file_name || doc.name}
                                  </td>
                                <td>{doc.department || "-"}</td>
                                <td>{doc.source_type || doc.source || "MANUAL"}</td>
                                <td>
                                  <span className={doc.verified ? "text-success fw-semibold" : "text-muted fw-semibold"}>
                                    {doc.verified ? "Verified" : "Not Verified"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="kb-modal-footer">
                  <span className="kb-selected-count">
                    {selectedDocs.length > 0
                      ? `${selectedDocs.length} document${selectedDocs.length > 1 ? "s" : ""} selected`
                      : "No documents selected"}
                  </span>
                  <div className="d-flex gap-2">
                    <button className="btn-kb-cancel" onClick={handleCloseKbModal}>Cancel</button>
                    <button
                      className="btn-kb-attach"
                      onClick={handleAttachDocs}
                      disabled={selectedDocs.length === 0}
                    >
                      Attach Selected
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

}
   
function Meta({ label, value }) {
  return (
    <div>
      <small className="text-muted" style={{ fontSize: "0.85rem" }}>{label}</small>
      <div className="fw-semibold text-dark mt-1" style={{ fontSize: "0.95rem" }}>
        {value !== "" && value !== null && value !== undefined ? value : "-"}
      </div>
    </div>
  );
}

export default ScreeningAgent;